import { sendDelete, sendGet, sendPost, sendPut } from '@shared/api/generic';
import {
    AgentExtensionSortBy,
    CreateAgentExtensionCommentRequest,
    McpServerCommentModel,
    McpServerRatingModel,
    OrganizationMcpServerUsageModel,
    RateAgentExtensionRequest,
} from '@shared/models/agentExtensions/AgentExtensionGovernanceModels';
import {
    AgentExtensionFilterRequest,
    CreateMcpServerRequest,
    EnableAgentExtensionRequest,
    PublishAgentExtensionRequest,
    UpdateMcpServerRequest,
} from '@shared/models/agentExtensions/AgentExtensionRequests';
import {
    AgentExtensionSubmissionModel,
    SubmitForReviewRequest,
} from '@shared/models/agentExtensions/AgentExtensionSubmissionModel';
import { McpOAuthCredentialsModel } from '@shared/models/agentExtensions/McpOAuthCredentialsModel';
import { McpServerRegistrationModel } from '@shared/models/agentExtensions/McpServerRegistrationModel';
import { UserEnabledMcpServerModel } from '@shared/models/agentExtensions/UserEnabledMcpServerModel';

/**
 * REST client for the API MCP-server endpoints (`/api/mcpServer/...`). Flat functions returning
 * `IResult<T>`, matching the `enabledPlayApi.ts` convention. Only NON-SECRET registration config is
 * ever exchanged — credentials stay on-device.
 */

const appendQuery = (params: Record<string, string | number | null | undefined>): string => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') return;
        search.append(key, value.toString());
    });
    const q = search.toString();
    return q ? `?${q}` : '';
};

export const getOfficialMcpServers = (modifiedSince?: string, sortBy?: AgentExtensionSortBy) =>
    sendGet<McpServerRegistrationModel[]>(`/api/mcpServer/official${appendQuery({ modifiedSince, sortBy })}`);

export const getFeaturedMcpServers = (modifiedSince?: string, sortBy?: AgentExtensionSortBy) =>
    sendGet<McpServerRegistrationModel[]>(`/api/mcpServer/featured${appendQuery({ modifiedSince, sortBy })}`);

export const getPublicMcpServers = (
    filter: AgentExtensionFilterRequest,
    skip = 0,
    take = 25,
    modifiedSince?: string,
    sortBy?: AgentExtensionSortBy
) =>
    sendGet<McpServerRegistrationModel[]>(
        `/api/mcpServer/public${appendQuery({
            searchPhrase: filter?.searchPhrase,
            category: filter?.category,
            skip,
            take,
            modifiedSince,
            sortBy,
        })}`
    );

export const getOrganizationMcpServers = (
    organizationId: string,
    modifiedSince?: string,
    sortBy?: AgentExtensionSortBy
) =>
    sendGet<McpServerRegistrationModel[]>(
        `/api/mcpServer/organization/${organizationId}${appendQuery({ modifiedSince, sortBy })}`
    );

/** Org-admin usage view: which members have each org MCP server enabled. */
export const getOrganizationMcpServerUsage = (organizationId: string) =>
    sendGet<OrganizationMcpServerUsageModel[]>(`/api/mcpServer/organization/${organizationId}/usage`);

export const getMyMcpServers = () => sendGet<McpServerRegistrationModel[]>(`/api/mcpServer/mine`);

export const getMcpServerById = (id: string) => sendGet<McpServerRegistrationModel>(`/api/mcpServer/${id}`);

export const createMcpServer = (request: CreateMcpServerRequest) =>
    sendPost<McpServerRegistrationModel>(`/api/mcpServer`, request);

export const updateMcpServer = (id: string, request: UpdateMcpServerRequest) =>
    sendPut<McpServerRegistrationModel>(`/api/mcpServer/${id}`, request);

export const deleteMcpServer = (id: string) => sendDelete<McpServerRegistrationModel>(`/api/mcpServer/${id}`);

export const publishMcpServer = (id: string, request: PublishAgentExtensionRequest) =>
    sendPost<McpServerRegistrationModel>(`/api/mcpServer/${id}/publish`, request);

// ----- public-review submissions -----

/** Submit an owned MCP server to Cephable for public-marketplace review (license-gated; not immediate). */
export const submitMcpServerForReview = (id: string, request: SubmitForReviewRequest = {}) =>
    sendPost<AgentExtensionSubmissionModel>(`/api/mcpServer/${id}/submit`, request);

/** Withdraw a pending submission and/or unlist a published MCP server. */
export const withdrawMcpServer = (id: string) =>
    sendPost<AgentExtensionSubmissionModel>(`/api/mcpServer/${id}/withdraw`, {});

/** The current (latest) review submission/status for an owned MCP server. */
export const getMcpServerSubmission = (id: string) =>
    sendGet<AgentExtensionSubmissionModel>(`/api/mcpServer/${id}/submission`);

export const enableMcpServer = (id: string, request: EnableAgentExtensionRequest) =>
    sendPost<UserEnabledMcpServerModel>(`/api/mcpServer/${id}/enable`, request);

export const disableMcpServer = (id: string, userDeviceId?: string) =>
    sendDelete<UserEnabledMcpServerModel>(`/api/mcpServer/${id}/enable${appendQuery({ userDeviceId })}`);

export const getEnabledMcpServers = (userDeviceId?: string) =>
    sendGet<UserEnabledMcpServerModel[]>(`/api/mcpServer/enabled${appendQuery({ userDeviceId })}`);

// ----- public (marketplace) ratings -----

export const getPublicMcpServerRatings = (mcpServerId: string) =>
    sendGet<McpServerRatingModel[]>(`/api/mcpServer/${mcpServerId}/ratings`);

export const ratePublicMcpServer = (mcpServerId: string, request: RateAgentExtensionRequest) =>
    sendPost<McpServerRatingModel>(`/api/mcpServer/${mcpServerId}/ratings`, request);

export const deletePublicMcpServerRating = (mcpServerId: string) =>
    sendDelete<McpServerRatingModel>(`/api/mcpServer/${mcpServerId}/ratings`);

// ----- org-internal ratings & comments -----

export const getMcpServerRatings = (mcpServerId: string, organizationId: string) =>
    sendGet<McpServerRatingModel[]>(`/api/mcpServer/${mcpServerId}/organization/${organizationId}/ratings`);

export const rateMcpServer = (mcpServerId: string, organizationId: string, request: RateAgentExtensionRequest) =>
    sendPost<McpServerRatingModel>(`/api/mcpServer/${mcpServerId}/organization/${organizationId}/ratings`, request);

export const deleteMcpServerRating = (mcpServerId: string, organizationId: string) =>
    sendDelete<McpServerRatingModel>(`/api/mcpServer/${mcpServerId}/organization/${organizationId}/ratings`);

export const getMcpServerComments = (mcpServerId: string, organizationId: string) =>
    sendGet<McpServerCommentModel[]>(`/api/mcpServer/${mcpServerId}/organization/${organizationId}/comments`);

export const createMcpServerComment = (
    mcpServerId: string,
    organizationId: string,
    request: CreateAgentExtensionCommentRequest
) => sendPost<McpServerCommentModel>(`/api/mcpServer/${mcpServerId}/organization/${organizationId}/comments`, request);

export const deleteMcpServerComment = (commentId: string) =>
    sendDelete<McpServerCommentModel>(`/api/mcpServer/comments/${commentId}`);

export const getMcpOAuthCredentials = (mcpServerId: string) =>
    sendGet<McpOAuthCredentialsModel>(`/api/mcpServer/${mcpServerId}/oauth-credentials`);
